import type { LiuFileStore, LiuImageStore } from "~/types"
import time from "../basic/time"
import { getCharacteristic } from "../liu-api/characteristic"
import valTool from "../basic/val-tool"

const MIN_3 = 3 * time.MINUTE
const IMG_SUFFIXES = ["png", "jpg", "jpeg", "gif", "webp"]

// 获取允许的图片类型 由 , 拼接而成的字符串
export function getAcceptImgTypesString() {
  const cha = getCharacteristic()

  // reference: https://blog.csdn.net/soslsboy/article/details/85229226
  if(cha.isAndroid) {
    return "image/*"
  }

  return "image/png,image/jpg,image/jpeg,image/gif,image/webp"
} 

export function getAcceptImgTypesArray() {
  const str = getAcceptImgTypesString()
  return str.split(",")
}

export function createObjURLs(files: Array<Blob | File>): string[] {
  const list = []
  for(let i=0; i<files.length; i++) {
    const v = files[i]
    const res = URL.createObjectURL(v)
    list.push(res)
  }

  return list
}


type UrlMapKey = string

interface UrlMapVal {
  createStamp: number
  usedStamp: number
  url: string
  num: number
}

const fileMap = new Map<UrlMapKey, UrlMapVal>()

export function createURLsFromStore(
  files: Array<LiuImageStore | LiuFileStore>,
) {
  const list: string[] = []
  for(let i=0; i<files.length; i++) {
    const v = files[i]
    const data = fileMap.get(v.id)
    const now = time.getTime()
    if(data) {
      list.push(data.url)
      data.usedStamp = now
      data.num++
      fileMap.set(v.id, data)
    }
    else if(v.arrayBuffer) {
      const blob = new Blob([v.arrayBuffer], { type: v.mimeType })
      const res = URL.createObjectURL(blob)
      list.push(res)
      const newData: UrlMapVal = {
        createStamp: now,
        usedStamp: now,
        url: res,
        num: 1,
      }
      fileMap.set(v.id, newData)
    }
    else if(v.cloud_url) {
      // 使用云端图片
      list.push(v.cloud_url)
    }
    else {
      list.push("")
    }
  }

  // 修剪 map
  _trimFileMap()

  return list
}

export function recycleURL(id: string, url: string) {
  const inMap = fileMap.has(id)
  if(inMap) {
    fileMap.delete(id)
  }
  URL.revokeObjectURL(url)
  return true
}


/**
 * 修剪 fileMap，控制 fileMap 的大小
 */
function _trimFileMap() {
  const size = fileMap.size

  const MAX_SIZE = 100
  if(size < MAX_SIZE) return
  
  const keys = fileMap.keys()
  for(const key of keys) {
    const data = fileMap.get(key)
    if(!data) continue
    if(time.isWithinMillis(data.usedStamp, MIN_3)) continue
    
    fileMap.delete(key)

    if(fileMap.size < MAX_SIZE) break 
  }
}

export function revokeObjURLs(urls: string[]) {
  for(let i=0; i<urls.length; i++) {
    const v = urls[i]
    URL.revokeObjectURL(v)
  }
  return true
}

export function getArrayFromFileList(fileList: FileList): File[] {
  const arr: File[] = []
  for(let i=0; i<fileList.length; i++) {
    const v = fileList[i]
    arr.push(v)
  }
  return arr
}

interface IsImageRes {
  result: boolean
  newFile?: File
}


function isImageFile(
  file: File,
): IsImageRes {
  const { type, name, lastModified } = file

  if(!type && name) {
    const suffix = valTool.getSuffix(name)
    if(!suffix) return { result: false }
    const suffixMatched = IMG_SUFFIXES.includes(suffix)
    if(!suffixMatched) return { result: false }
    const newFile = new File([file], name, { 
      type: `image/${suffix}`,
      lastModified,
    })

    return { result: true, newFile }
  }

  const arr = getAcceptImgTypesArray()
  if(arr.includes(type)) return { result: true }
  const prefixMatched = type.startsWith("image/")
  if(!prefixMatched) return { result: false }
  return { result: true }
}

export function getOnlyImageFiles(files: File[]): File[] {
  const imgFiles: File[] = []
  for(let i=0; i<files.length; i++) {
    const v = files[i]
    const res1 = isImageFile(v)
    if(!res1.result) continue
    const { newFile } = res1
    if(newFile) imgFiles.push(newFile)
    else imgFiles.push(v)
  }
  return imgFiles
}

export function getNotImageFiles(files: File[]): File[] {
  const newList: File[] = []
  for(let i=0; i<files.length; i++) {
    const v = files[i]
    if(!isImageFile(v)) newList.push(v)
  }
  return newList
}

export function constraintWidthHeight(
  w: number,
  h: number,
  maxW: number,
  maxH: number,
) {
  if (w > maxW && w > h) {
    return {
      width: maxW,
      height: Math.round(h * (maxW / w))
    }
  }
  if (h > maxH) {
    return {
      width: Math.round( w * (maxH / h)),
      height: maxH
    }
  }
  return { width: w, height: h }
}